import { usePageHeader } from "@/containers/MobileHeaderContext";
import { SquadWithPanel } from "@/features/MyClub/ActiveSquad/SquadWithPanel";
import { StartImport } from "@/features/MyClub/StartImport";
import {
  TryNextHeader,
  useTryNext,
} from "@/features/MyClub/TryNext/TryNextSection";
import { MyClubWelcomeDialog } from "@/features/MyClub/Welcome/MyClubWelcomeDialog";
import { cn } from "@/lib/utils";
import { BREAKPOINTS } from "@/settings";
import { safeGetItem, safeSetItem } from "@/utils/safeStorage";
import { useTranslation } from "@repo/shared/i18n";
import { clubStatsSelector } from "@repo/shared/redux";
import { formatTime, formatTimeAgoShort } from "@repo/shared/utils";
import { PanelLeft, X } from "lucide-react";
import { ReactNode, useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import { useSelector } from "react-redux";
import { Outlet, useLocation } from "react-router";
import { useMediaQuery } from "usehooks-ts";

import {
  ClubValuePopover,
  MyClubSidePanel,
  clubCrestUrl,
} from "./MyClubSidePanel";

const RAIL_COLLAPSED_KEY = "MY_CLUB_RAIL_COLLAPSED";

const SECTION_IDS = { squad: "active-squad", tryNext: "try-next" };

/** The club home: the active squad, with what to try next beside it. */
const MyClubHome = () => {
  const { cards, done, total, showProgress } = useTryNext();
  return (
    <>
      <SquadWithPanel
        squad={{ enabled: true, id: SECTION_IDS.squad }}
        header={
          <TryNextHeader
            done={done}
            total={total}
            showProgress={showProgress}
          />
        }
        panelId={SECTION_IDS.tryNext}
      >
        {cards}
      </SquadWithPanel>
      <MyClubWelcomeDialog />
    </>
  );
};

// The secondary icon button's fill: easy to miss as a plain dark square.
const PanelButton = ({
  onClick,
  label,
}: {
  onClick: () => void;
  label: string;
}) => (
  <button
    onClick={onClick}
    title={label}
    aria-label={label}
    className="flex size-8 flex-none items-center justify-center rounded-lg border border-neutral-600 bg-[var(--color-secondaryy)] text-on-secondary transition-colors hover:bg-[var(--color-secondaryy-hover)] mobile:size-7 mobile:rounded-lg"
  >
    <PanelLeft className="size-4 mobile:size-4" strokeWidth={2} />
  </button>
);

/** The side panel as a drawer, for screens too narrow to keep it open. */
const MobileDrawer = ({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}) => {
  const { t } = useTranslation("myClub");

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  return (
    <div
      className={cn(
        "fixed inset-0 z-50 transition-[visibility] duration-200",
        open ? "visible" : "invisible",
      )}
      aria-hidden={!open}
    >
      <div
        onClick={onClose}
        className={cn(
          "absolute inset-0 bg-black/60 backdrop-blur-xs transition-opacity duration-200",
          open ? "opacity-100" : "opacity-0",
        )}
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-label={t("nav.menu")}
        className={cn(
          "absolute inset-y-0 left-0 w-72 max-w-[85vw] border-r border-neutral-800 bg-streamline-surface transition-transform duration-200 ease-out",
          open ? "translate-x-0" : "-translate-x-full",
        )}
      >
        <button
          onClick={onClose}
          aria-label={t("nav.close")}
          className="absolute top-2 right-2 z-10 flex size-11 items-center justify-center text-neutral-400 hover:text-neutral-50"
        >
          <X className="size-4" />
        </button>
        <MyClubSidePanel onNavigate={onClose} />
      </div>
    </div>
  );
};

/**
 * My Club for someone with a club: a side panel for getting around the club,
 * a header with the re-import, and the page itself — the home or one of its
 * sub-pages. On mobile the panel turns into a drawer.
 */
export const MyClubShell = ({
  helmetTitle,
  helmetDescription,
  helmetCanonicalLink,
  sectionTitle,
  toolbar,
  fullBleed = false,
}: {
  helmetTitle: string;
  helmetDescription?: string;
  helmetCanonicalLink?: string;
  /** The sub-page's name; empty on the home. */
  sectionTitle: string;
  /** Sub-page actions, shown in a row under the header. */
  toolbar?: ReactNode;
  /** The page fills the content area edge to edge (e.g. My Players' table). */
  fullBleed?: boolean;
}) => {
  const { t } = useTranslation("myClub");
  const location = useLocation();
  const isMobile = useMediaQuery(BREAKPOINTS.MOBILE.query);
  const stats = useSelector(clubStatsSelector);
  /*
    Below large desktop the content needs the room, so the panel starts as a
    rail; opening it there lasts for the visit. On wider screens the user's
    own choice is remembered.
  */
  const isSmallDesktop = useMediaQuery(BREAKPOINTS.SMALL_DESKTOP.query);
  const [wideCollapsed, setWideCollapsed] = useState(
    () => safeGetItem(RAIL_COLLAPSED_KEY) === "true",
  );
  const [narrowCollapsed, setNarrowCollapsed] = useState(true);
  const collapsed = isSmallDesktop ? narrowCollapsed : wideCollapsed;
  const [drawerOpen, setDrawerOpen] = useState(false);
  const isHome = location.pathname === "/my-club";

  const toggleRail = () => {
    if (isSmallDesktop) {
      setNarrowCollapsed((c) => !c);
      return;
    }
    setWideCollapsed((c) => {
      safeSetItem(RAIL_COLLAPSED_KEY, String(!c));
      return !c;
    });
  };

  // Hash links from the panel land on a section of the home.
  useEffect(() => {
    if (!isHome || !location.hash) return;
    document
      .getElementById(location.hash.slice(1))
      ?.scrollIntoView({ behavior: "smooth", block: "start" });
  }, [isHome, location.hash]);

  // The top bar names the club, as the app's nav bar does.
  usePageHeader({
    h1: stats.clubName || t("home.title"),
    icon: stats.clubBadgeId ? clubCrestUrl(stats.clubBadgeId) : undefined,
  });

  const imported =
    stats.lastImportedAt > 0
      ? t("home.imported", {
          time: formatTime(Date.now() - stats.lastImportedAt),
        })
      : "";

  return (
    <>
      <Helmet>
        <title>{helmetTitle}</title>
        {helmetDescription && (
          <meta name="description" content={helmetDescription} />
        )}
        <meta property="og:title" content={helmetTitle} />
        {helmetDescription && (
          <meta property="og:description" content={helmetDescription} />
        )}
        {helmetCanonicalLink && (
          <link rel="canonical" href={helmetCanonicalLink} />
        )}
      </Helmet>

      {/* The panel sits inside the content width, like the rest of the app,
          rather than against the window's edge. */}
      <div className="min-h-screen bg-neutral-900 text-neutral-100">
        <div className="mx-auto flex min-h-screen w-full max-w-content items-stretch">
          {!isMobile && (
            <aside
              className={cn(
                "sticky top-12 h-[calc(100dvh-3rem)] flex-none overflow-hidden border-r border-neutral-800 bg-streamline-surface transition-[width] duration-200 ease-out",
                collapsed ? "w-15" : "w-59",
              )}
            >
              <MyClubSidePanel collapsed={collapsed} />
            </aside>
          )}

          <div className="flex min-w-0 flex-1 flex-col">
            <header className="flex items-center gap-2.5 border-b border-neutral-800 bg-neutral-950 px-4 py-3 mobile:h-15 mobile:gap-3.5 mobile:px-6 mobile:py-0">
              {isMobile ? (
                <>
                  <PanelButton
                    onClick={() => setDrawerOpen(true)}
                    label={t("nav.menu")}
                  />
                  {/* The club itself is named in the top bar. */}
                  {sectionTitle ? (
                    <h2 className="truncate text-base font-semibold">
                      {sectionTitle}
                    </h2>
                  ) : (
                    <ClubValuePopover stats={stats} stacked />
                  )}
                  {isHome && (
                    <div className="ml-auto flex flex-none items-center gap-2">
                      {stats.lastImportedAt > 0 && (
                        <span className="text-xs text-foreground-muted">
                          {formatTimeAgoShort(
                            Date.now() - stats.lastImportedAt,
                          )}
                        </span>
                      )}
                      <StartImport
                        disabled={false}
                        variant="tertiary"
                        size="small"
                      />
                    </div>
                  )}
                </>
              ) : (
                <>
                  <PanelButton
                    onClick={toggleRail}
                    label={t("nav.toggleSidebar")}
                  />
                  <h1 className="text-base font-semibold tracking-wide">
                    {sectionTitle || t("home.title")}
                  </h1>
                  <div className="ml-auto flex flex-none items-center gap-3">
                    {/* Beside the button it dates. */}
                    {imported && (
                      <span className="text-xs text-foreground-muted">
                        {imported}
                      </span>
                    )}
                    <StartImport
                      disabled={false}
                      variant="secondary"
                      size="small"
                    />
                  </div>
                </>
              )}
            </header>

            {toolbar && (
              <div className="flex items-center justify-end gap-2 border-b border-neutral-800 px-4 py-2 mobile:px-6">
                {toolbar}
              </div>
            )}

            <main
              className={cn(
                !fullBleed && "px-4 pt-4 pb-7 mobile:px-6 mobile:py-5",
              )}
            >
              {isHome ? <MyClubHome /> : <Outlet />}
            </main>
          </div>
        </div>
      </div>

      {isMobile && (
        <MobileDrawer open={drawerOpen} onClose={() => setDrawerOpen(false)} />
      )}
    </>
  );
};
